import { mapScheduleToTRPC } from '@risksmart-app/trpc/src/types';
import type { InsertChildControlMutation } from '@risksmart-app/web-graphql-client/generated/graphql';
import { InsertChildControlDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { createMutationHook } from '@/utils/hook-factories';

import type { FrontendRouterInputs } from '../routerInputs';
import type { InsertControlInput } from '../scheduledEntityInputs';

type InsertControlTRPCInput = FrontendRouterInputs['control']['insert'];

export const useInsertControl = createMutationHook({
  name: 'insertControl',
  isIdempotentCreate: true,
  mutationOptions: (trpc, onSuccess, requestContext) =>
    trpc.frontend.control.insert.mutationOptions({
      onSuccess,
      trpc: { context: requestContext },
    }),
  mapInput: ({
    schedule,
    ...rest
  }: InsertControlInput): InsertControlTRPCInput => ({
    ...rest,
    ...mapScheduleToTRPC(schedule),
  }),
  invalidate: (trpc) => [
    trpc.frontend.control.register.queryKey(),
    trpc.frontend.control.controlById.queryKey(),
    trpc.frontend.control.controlsByUserId.queryKey(),
    trpc.frontend.control.controlsBasic.queryKey(),
    // COI-889: the left-nav badge reads this count family.
    trpc.frontend.control.controlCount.queryKey(),
    // COI-889: the risk register's linked-control count and Associations
    // column read these; the Apollo twin evicts `risk` and `linked_item`.
    trpc.frontend.risk.register.queryKey(),
    trpc.frontend.linkedItem.linkedItems.queryKey(),
    // COI-889: the my-items ribbon counts this entity.
    trpc.frontend.myItems.pathKey(),
  ],
  mapResponse: (result: { Id: string }): InsertChildControlMutation => ({
    insertChildControl: {
      __typename: 'IdOutput',
      Id: result.Id,
    },
  }),
  graphqlDocument: InsertChildControlDocument,
  graphqlVariables: (input: InsertControlInput) => ({ object: input }),
  evictFields: [
    'risk',
    'control',
    'control_aggregate',
    'obligation',
    'linked_item',
    'risk_score',
  ],
});
